import React, { useState } from "react";
import { toast } from "sonner";
import { QrCode, Loader2 } from "lucide-react";
import { useCheckin } from "@/hooks/useRewards";
import { REWARDS } from "@/constants/testIds";

/**
 * Paste-code check-in card (web variant — no camera). The paste-code hint
 * points customers at the QR poster at the counter; trailing whitespace is
 * trimmed inside the mutation to be forgiving of copy-paste.
 */
export default function CheckinCard() {
  const [token, setToken] = useState("");
  const mut = useCheckin();

  const submit = async (e) => {
    e.preventDefault();
    const t = token.trim();
    if (!t) {
      toast.error("Paste the counter code first.");
      return;
    }
    try {
      const data = await mut.mutateAsync(t);
      const first = data?.first_visit_points || 0;
      const code = data?.discount_code;
      toast.success(
        first > 0
          ? `Welcome — +${first} points!${code ? ` Code: ${code}` : ""}`
          : `Checked in.${code ? ` Code: ${code}` : ""}`
      );
      setToken("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Check-in failed — is the code fresh?");
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid={REWARDS.checkinCard}
      className="rounded-2xl bg-white shadow-card p-5 md:p-6"
    >
      <div className="flex items-center gap-2 text-sm font-display font-bold text-slate-900 mb-1">
        <QrCode className="w-4 h-4 text-[color:var(--brand-blue)]" /> Check in at a store
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Paste the counter code shown on the QR poster at the store.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          data-testid={REWARDS.checkinInput}
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="RW1.store123.abc.def"
          className="input flex-1 font-mono text-sm"
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
        />
        <button
          type="submit"
          data-testid={REWARDS.checkinBtn}
          disabled={mut.isPending}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold px-5 py-2.5 disabled:opacity-60 transition-colors"
        >
          {mut.isPending && <Loader2 className="w-4 h-4 animate-spin" />} Check in
        </button>
      </div>
      <p className="mt-2 text-[11px] text-slate-400">
        Find this code on the store&apos;s counter QR poster.
      </p>
    </form>
  );
}
